import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'wouter';
import { artistFromStartParam } from './lib/telegram';
import { AppLayout, artistNav, customerNav } from './components/layout';
import { SessionGate } from './components/session-gate';
import { LoadingScreen } from './components/ui';
import { ApiError, api } from './lib/api';
import { ApplicationsPage } from './pages/applications-page';
import { ArtistSearchPage } from './pages/artist-search-page';
import { CalendarPage } from './pages/calendar-page';
import { ClientPage } from './pages/client-page';
import { ClientsPage } from './pages/clients-page';
import { CustomerApplicationsPage } from './pages/customer-applications-page';
import { CustomerOrderPage } from './pages/customer-order-page';
import { CustomerOrdersPage } from './pages/customer-orders-page';
import { CustomerSettingsPage } from './pages/customer-settings-page';
import { DashboardPage } from './pages/dashboard-page';
import { OnboardingPage } from './pages/onboarding-page';
import { OrderPage } from './pages/order-page';
import { OrdersPage } from './pages/orders-page';
import { ProfilePage } from './pages/profile-page';
import { PublicArtistPage } from './pages/public-artist-page';
import { AnalyticsPage } from './pages/analytics-page';
import { QueuePage } from './pages/queue-page';
import type { ActionCenter, ArtistProfile, User } from './types';

export function App() {
  const [location, setLocation] = useLocation();

  // Пришли по ссылке `t.me/бот?startapp=artist_<slug>`: Telegram открыл Mini App,
  // а страницу художника выбираем уже здесь — тогда заявка уйдёт подписанной.
  useEffect(() => {
    const slug = artistFromStartParam();

    if (slug && location === '/') {
      setLocation(`/a/${slug}`, { replace: true });
    }
    // Разово при запуске: дальше пользователь ходит по приложению сам.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Switch>
      <Route path="/a/:slug" component={PublicArtistPage} />
      <Route>
        <SessionGate>{(user) => <Workspace user={user} />}</SessionGate>
      </Route>
    </Switch>
  );
}

/**
 * Один пользователь может быть и художником, и заказчиком (раздел 7 ТЗ).
 * Кабинет заказчика доступен всем, кабинет художника — при наличии профиля.
 */
function Workspace({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api<ArtistProfile>('/artist/profile'),
    retry: false,
  });
  const actions = useQuery({
    queryKey: ['actions'],
    queryFn: () => api<ActionCenter>('/artist/actions'),
    // У заказчика профиля художника нет — запрос вернул бы 404.
    enabled: profile.data !== undefined,
  });

  if (profile.isPending) return <LoadingScreen label="Открываем рабочую область" />;

  const missingProfile =
    profile.error instanceof ApiError && profile.error.code === 'PROFILE_NOT_FOUND';

  if (!missingProfile && profile.isError) throw profile.error;

  // Онбординг художника открывается только по явному переходу: заказчику он не нужен.
  if (missingProfile && location.startsWith('/become-artist')) {
    return (
      <OnboardingPage
        user={user}
        onComplete={(created) => {
          queryClient.setQueryData(['profile'], created);
          void queryClient.invalidateQueries();
        }}
      />
    );
  }

  const artistProfile = profile.data;
  if (!artistProfile) {
    return (
      <AppLayout nav={customerNav} switcher={<BecomeArtistLink />}>
        <CustomerRoutes fallback="/my" />
      </AppLayout>
    );
  }

  const inCustomerCabinet = location.startsWith('/my');
  // Тот же запрос, что и у центра действий на главной: счётчики и список дел
  // должны показывать одно и то же число, а не расходиться на своих правилах.
  const artistBadges = {
    '/applications': actions.data?.counts.applications ?? 0,
    '/orders': actions.data?.counts.orders ?? 0,
  };

  return (
    <AppLayout
      nav={inCustomerCabinet ? customerNav : artistNav}
      switcher={<CabinetSwitcher inCustomerCabinet={inCustomerCabinet} />}
      badges={inCustomerCabinet ? undefined : artistBadges}
    >
      {inCustomerCabinet ? (
        <CustomerRoutes fallback="/my" />
      ) : (
        <Switch>
          <Route path="/">{() => <DashboardPage profile={artistProfile} />}</Route>
          <Route path="/applications" component={ApplicationsPage} />
          <Route path="/orders" component={OrdersPage} />
          <Route path="/orders/:id" component={OrderPage} />
          <Route path="/clients" component={ClientsPage} />
          <Route path="/clients/:id" component={ClientPage} />
          <Route path="/queue" component={QueuePage} />
          <Route path="/analytics" component={AnalyticsPage} />
          <Route path="/calendar" component={CalendarPage} />
          <Route path="/profile">{() => <ProfilePage profile={artistProfile} />}</Route>
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      )}
    </AppLayout>
  );
}

function CustomerRoutes({ fallback }: { fallback: string }) {
  return (
    <Switch>
      <Route path="/my" component={CustomerOrdersPage} />
      <Route path="/my/applications" component={CustomerApplicationsPage} />
      <Route path="/my/artists" component={ArtistSearchPage} />
      <Route path="/my/orders/:id" component={CustomerOrderPage} />
      <Route path="/my/settings" component={CustomerSettingsPage} />
      <Route>
        <Redirect to={fallback} />
      </Route>
    </Switch>
  );
}

function CabinetSwitcher({ inCustomerCabinet }: { inCustomerCabinet: boolean }) {
  return (
    <Link href={inCustomerCabinet ? '/' : '/my'} className="plan-pill plan-pill--link">
      {inCustomerCabinet ? 'Я художник' : 'Я заказчик'}
    </Link>
  );
}

function BecomeArtistLink() {
  return (
    <Link href="/become-artist" className="plan-pill plan-pill--link">
      Стать художником
    </Link>
  );
}
